import { Navigate, type RouteObject } from 'react-router';

import { AppShell } from '@/components/layout/app-shell';
import { BackendUnreachable } from '@/components/backend-unreachable';
import { NAV_GROUPS } from '@/config/navigation';
import { InvestorGate, RequireAuth, useCurrentUser } from '@/features/auth';
import DesignTokensRoute from '@/routes/design-tokens/route';
import PlaceholderRoute from '@/routes/_placeholder/route';
import RootErrorBoundary from '@/routes/root-error';

import { NESTED_ROUTES } from './route-tree.nested';

function IndexRedirect() {
  const { isLoading, isError, isBackendUnreachable, user, isInvestorOnly } = useCurrentUser();
  if (isLoading) return null;
  // Defense-in-depth: InvestorGate already catches the unreachable state
  // before this route mounts. This branch only matters if the gate order
  // changes — a network-level failure must never read as "signed out",
  // because the stored token bounces /sign-in straight back in a loop.
  if (isBackendUnreachable) return <BackendUnreachable />;
  if (isError || !user) return <Navigate to="/sign-in" replace />;
  return <Navigate to={isInvestorOnly ? '/finance/gmv' : '/companies'} replace />;
}

const REAL_ROUTES: Record<string, RouteObject['element']> = {
  'design-tokens': <DesignTokensRoute />,
};

// Registered below as explicit children; nav items with these slugs must not
// also spawn a placeholder route, which would shadow the real one.
const INVOICING_ROUTES: RouteObject[] = [
  {
    path: 'invoicing/open-provisional',
    lazy: () =>
      import('./routes/invoicing/open-provisional').then((m) => ({ Component: m.default })),
  },
  {
    path: 'invoicing/master',
    lazy: () => import('./routes/invoicing/master').then((m) => ({ Component: m.default })),
  },
];

const EXPLICIT_ROUTE_PATHS = new Set(INVOICING_ROUTES.map((route) => route.path));

const navRoutes: RouteObject[] = NAV_GROUPS.flatMap((group) => group.items)
  .filter((item) => !EXPLICIT_ROUTE_PATHS.has(item.slug))
  .map((item) => {
    const nested = NESTED_ROUTES[item.slug];
    if (nested) return nested;
    return {
      path: item.slug,
      element: REAL_ROUTES[item.slug] ?? <PlaceholderRoute />,
    };
  });

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RequireAuth>
        <InvestorGate>
          <AppShell />
        </InvestorGate>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <IndexRedirect /> },
      ...navRoutes,
      {
        path: 'profile',
        lazy: () => import('./routes/profile/route').then((m) => ({ Component: m.default })),
      },
      {
        path: 'admin/users',
        lazy: () => import('./routes/admin/users/route').then((m) => ({ Component: m.default })),
      },
      // Not in the sidebar (so not in navRoutes) - register explicitly.
      ...(NESTED_ROUTES['settings/approval-rules']
        ? [NESTED_ROUTES['settings/approval-rules']]
        : []),
      ...(NESTED_ROUTES['settings/locations'] ? [NESTED_ROUTES['settings/locations']] : []),
      {
        path: 'settings/partner-acceptance',
        lazy: () =>
          import('./routes/settings/partner-acceptance/route').then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: 'settings/partner-disqualified',
        lazy: () =>
          import('./routes/settings/partner-disqualified/route').then((m) => ({
            Component: m.default,
          })),
      },
      // Not in the sidebar. Registered here because route-tree.nested.tsx is at its max-lines cap.
      {
        path: 'settings/eligibility-rules',
        children: [
          {
            index: true,
            lazy: () =>
              import('./routes/settings/eligibility-rules/route').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'new',
            lazy: () =>
              import('./routes/settings/eligibility-rules/new/route').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: ':id',
            lazy: () =>
              import('./routes/settings/eligibility-rules/$id/route').then((m) => ({
                Component: m.default,
              })),
          },
        ],
      },
      // This is a separate top-level entry so the rule-testing route never depends on the rule-setup route registration.
      {
        path: 'settings/eligibility-rules/test',
        children: [
          {
            index: true,
            lazy: () =>
              import('./routes/settings/eligibility-rules/test/route').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: ':runId',
            lazy: () =>
              import('./routes/settings/eligibility-rules/test/$runId/route').then((m) => ({
                Component: m.default,
              })),
          },
        ],
      },
      {
        path: 'documents/:id/edit',
        lazy: () =>
          import('./routes/documents/$id-edit/route').then((m) => ({ Component: m.default })),
      },
      {
        path: 'bl-orders/:blOrderId',
        lazy: () => import('./routes/bl-orders/$blOrderId').then((m) => ({ Component: m.default })),
      },
      {
        path: 'bl-orders/:blOrderId/extraction/edit',
        lazy: () =>
          import('./routes/bl-orders/$blOrderId-extraction-edit').then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: 'bl-orders/:blOrderId/other-costs/new',
        lazy: () =>
          import('./routes/bl-orders/$blOrderId-other-costs-new').then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: 'bl-orders/:blOrderId/other-costs/:otherCostId/edit',
        lazy: () =>
          import('./routes/bl-orders/$blOrderId-other-costs-edit').then((m) => ({
            Component: m.default,
          })),
      },
      ...INVOICING_ROUTES,
      {
        path: '_dev/files-demo',
        lazy: () =>
          import('./routes/_dev/files-demo/route').then((m) => ({ Component: m.default })),
      },
    ],
  },
  {
    path: '/forgot-password',
    lazy: () => import('./routes/forgot-password/route').then((m) => ({ Component: m.default })),
  },
  {
    path: '/reset-password',
    lazy: () => import('./routes/reset-password/route').then((m) => ({ Component: m.default })),
  },
  {
    path: '/sign-in',
    lazy: () => import('./routes/sign-in/route').then((m) => ({ Component: m.default })),
  },
  {
    path: '/sign-in/magic-link',
    lazy: () => import('./routes/sign-in.magic-link/route').then((m) => ({ Component: m.default })),
  },
  {
    path: '/sign-in/magic',
    lazy: () => import('./routes/sign-in.magic/route').then((m) => ({ Component: m.default })),
  },
  {
    path: '/accept-invitation',
    lazy: () => import('./routes/accept-invitation/route').then((m) => ({ Component: m.default })),
  },
];
